import React, { useEffect, useState } from "react";
import {
    DataGrid,
    GridActionsCellItem,
    GridRowModes,
    GridRowModesModel,
    GridRowModel,
    GridRowParams,
    GridColDef,
    GridRowId
} from "@mui/x-data-grid";
import type { GridEventListener } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import ApiService from "../../../services/ApiService";
import { messageStore } from "../../../app/common/messages/MessageStore";

interface MessageWall {
    id: number;
    message: string;
    display: boolean;
}

export const MessageWallAdmin: React.FC = () => {
    const [rows, setRows] = useState<MessageWall[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

    const handleRowEditStart: GridEventListener<'rowEditStart'> = (params, event) => {
        event.defaultMuiPrevented = true;
    };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        event.defaultMuiPrevented = true;
    };


    const handleEditClick = (id: GridRowId) => {
        setRowModesModel((prev: GridRowModesModel) => ({
            ...prev,
            [id]: { mode: GridRowModes.Edit },
        }));
    };

    const handleSaveClick = (id: GridRowId) => {
        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: GridRowModes.View },
        }));
    };

    const handleCancelClick = (id: GridRowId) => {
        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
    };

    const fetchMessages = () => {
        setLoading(true);
        ApiService.get(`${ApiService.API_URL}admin-tools/message-wall`)
            .then((response) => {
                setRows(response.data);
            })
            .catch(() => {
                messageStore.setErrorMessage("Failed to load messages.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleRowUpdate = (
        updatedRow: GridRowModel,
        oldRow: GridRowModel
    ) => {
        return ApiService.put(
            `${ApiService.API_URL}admin-tools/message-wall/${updatedRow.id}`,
            updatedRow
        )
            .then((response) => {
                messageStore.setInfoMessage("Updated row");

                setRows((prevRows) =>
                    prevRows.map((row) =>
                        row.id === updatedRow.id ? { ...row, ...updatedRow } : row
                    )
                );

                return response.data;
            })
            .catch((error) => {
                messageStore.setErrorMessage("Update failed");
                throw error;
            });
    };


    useEffect(() => {
        fetchMessages();
    }, []);

    const columns :GridColDef[] = [
        {
            field: "message",
            headerName: "Wall Message",
            flex: 1,
            editable: true,
        },
        {
            field: "display",
            headerName: "Is Displayed",
            type: "boolean",
            width: 150,
            editable: true,
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            getActions: (params: GridRowParams) => {
                const isInEditMode =
                    rowModesModel[params.id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            onClick={() => handleSaveClick(params.id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            onClick={() => handleCancelClick(params.id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        onClick={() => handleEditClick(params.id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onRowEditStart={handleRowEditStart}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={handleRowUpdate}
            onProcessRowUpdateError={() => {
                messageStore.setErrorMessage("Update error");
            }}
            autoHeight
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
        />
    );
};